//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import styled from "@emotion/styled";
import { isEmpty, omit } from "lodash";
import React, { useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Button, Checkbox, CheckboxProps, Form, Icon, Message, Modal } from "semantic-ui-react";
import { DeploymentNotificationSummary } from "../../api/data/v1/idata";
import { ListAllDeploymentsRequest } from "../../api/lib";
import { ListAllOrganizationsRequest, OrganizationNotificationSummary } from "../../api/resourcemanager/v1/iresourcemanager";
import { ContentSegment } from "../../ui/_style";
import apiClients from "../../api/apiclients";
import { ErrorMessage, FormActionButtonCancel, FormActionButtonCreate, FormActionButtonSave } from "../../ui/lib";
import { IWithRefreshProps } from "../../util/WithRefresh";
import DeploymentList from "../deployment/DeploymentList";
import { NotificationData, NotificationFields, emptyNotificationData } from "../notifications/NotificationFields";
import AssignedFiltersForJobView from "../deployment-updater/AssignedFiltersForJobView";
import OrganizationList from "../organization/OrganizationList";
import { builtInNotifications } from "./builtInNotifications";
import {
  NotificationRule,
  NotificationRuleTarget,
  filtersTitle,
  ruleNotificationIdFormat,
  ruleScope,
  targetLabels,
  toDeploymentRule,
  toOrganizationRule,
} from "./types";

const StyledFilterHeader = styled.div`
  display: block;
  color: #879191;
  font-size: 0.85714286rem;
  font-weight: 700;
  text-transform: none;
  padding: 0 24px;
  position: relative;
  top: 20px;
`;

const StyledIcon = styled(Icon)`
  position: relative;
  top: -5px;
`;

export interface CreateNotificationRuleModalArgs extends IWithRefreshProps, RouteComponentProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  // Rule to edit; a new rule is created when not set.
  existing?: NotificationRule;
  // Targets the caller may create rules for (create permission); ignored when editing.
  targets: NotificationRuleTarget[];
  // Notifications currently present on the platform, to warn when a new rule takes over an existing ID.
  // Empty for a target whose summaries the caller may not list: the warning then counts only the other one.
  deploymentSummaries: DeploymentNotificationSummary[];
  organizationSummaries: OrganizationNotificationSummary[];
}

// CreateNotificationRuleModal creates or edits a platform-wide notification rule: an operator-written
// message shown on every deployment or organization matching a filter.
// When editing, the target, the notification ID and the scope are fixed.
const CreateNotificationRuleModal = (props: CreateNotificationRuleModalArgs) => {
  const { existing } = props;
  const [error, setError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [target, setTarget] = useState<NotificationRuleTarget>(existing ? existing.target : props.targets[0] || "deployment");
  const [notification, setNotification] = useState<NotificationData>(existing ? existing.notification : emptyNotificationData());
  const [enabled, setEnabled] = useState(existing ? existing.enabled : true);
  const [deploymentFilter, setDeploymentFilter] = useState<ListAllDeploymentsRequest>({});
  const [organizationFilter, setOrganizationFilter] = useState<ListAllOrganizationsRequest>({});
  const [showScope, setShowScope] = useState(false);
  // Explicit opt-in for an empty scope, so nobody notifies the whole platform by forgetting a filter.
  const [applyToAll, setApplyToAll] = useState(false);

  const save = async () => {
    if (!notification.notificationId || !notification.notificationText) {
      setError("Notification ID and Notification text are required");
      return;
    }
    if (!existing) {
      if (!ruleNotificationIdFormat.pattern.test(notification.notificationId)) {
        setError(`Invalid Notification ID: ${ruleNotificationIdFormat.hint}, e.g. ${ruleNotificationIdFormat.example}`);
        return;
      }
      if (builtInNotifications[notification.notificationId]) {
        setError(`Notification ID ${notification.notificationId} is reserved by a built-in inspector`);
        return;
      }
    }
    let rule: NotificationRule;
    if (existing) {
      // The stored scope is sent back unchanged.
      rule = { ...existing, notification, enabled };
    } else {
      const scope = applyToAll ? {} : target === "organization" ? organizationFilter : deploymentFilter;
      if (!applyToAll && isEmpty(scope)) {
        setError(`Select at least one filter, or enable "Apply to all ${target}s" explicitly`);
        return;
      }
      rule = {
        id: "",
        url: "",
        target,
        notification,
        deployment_filter: target === "deployment" ? scope : undefined,
        organization_filter: target === "organization" ? scope : undefined,
        enabled: true,
        created_by_id: "",
        matched: 0,
      };
    }
    setError(undefined);
    setSaving(true);
    try {
      const client = apiClients.idashboardClient;
      if (rule.target === "organization") {
        existing
          ? await client.UpdateOrganizationNotificationRule(toOrganizationRule(rule))
          : await client.CreateOrganizationNotificationRule(toOrganizationRule(rule));
      } else {
        existing
          ? await client.UpdateDeploymentNotificationRule(toDeploymentRule(rule))
          : await client.CreateDeploymentNotificationRule(toDeploymentRule(rule));
      }
    } catch (e) {
      setError(String(e));
      setSaving(false);
      return;
    }
    setSaving(false);
    props.onSaved();
    props.onClose();
  };

  const existingScope = existing ? ruleScope(existing) : {};

  // How many resources already carry the typed notification ID (manually or otherwise); a new rule takes it over.
  const carriedCount = (summaries: { notification_id?: string; count?: number }[]) =>
    summaries.filter((s) => s.notification_id === notification.notificationId).reduce((n, s) => n + (s.count || 0), 0);
  const carriedDeployments = existing || !notification.notificationId ? 0 : carriedCount(props.deploymentSummaries);
  const carriedOrganizations = existing || !notification.notificationId ? 0 : carriedCount(props.organizationSummaries);

  return (
    <Modal size="fullscreen" open={props.open}>
      <Modal.Header>{existing ? "Edit platform notification rule" : "Create platform notification rule"}</Modal.Header>
      <Modal.Content scrolling>
        <ContentSegment>
          <ErrorMessage active={!!error} message={error} onDismiss={() => setError(undefined)} />
          <Form>
            <Form.Group inline>
              <label>Target</label>
              {(existing ? [existing.target] : props.targets).map((key) => (
                <Form.Radio key={key} label={targetLabels[key]} value={key} checked={target === key} disabled={!!existing} onChange={() => setTarget(key)} />
              ))}
            </Form.Group>
            <Message info size="small">
              Shown on every {target} matching the filters below for as long as the rule is enabled and not expired, and removed automatically from those that
              stop matching. Reconciled by {target === "organization" ? "organization-inspector" : "deployment-inspector"}. Notifications driven by a condition
              that is not a filter (version below minimum, read-only, VST, billing) stay built into the inspectors and are configured from the config repo.
            </Message>
            <NotificationFields value={notification} onChange={setNotification} idReadOnly={!!existing} idFormat={ruleNotificationIdFormat} />
            {carriedDeployments + carriedOrganizations > 0 && (
              <Message warning size="small">
                <Icon name="warning sign" />
                {carriedDeployments} deployments / {carriedOrganizations} organizations already carry <code>{notification.notificationId}</code>. The rule takes
                this notification over: it will be rewritten inside the scope and removed everywhere else.
              </Message>
            )}
            {existing && (
              <>
                <Form.Field>
                  <Checkbox
                    toggle
                    label="Enabled"
                    checked={enabled}
                    onChange={(event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => setEnabled(!!data.checked)}
                  />
                </Form.Field>
                <Form.Field>
                  <label>Scope</label>
                  {isEmpty(existingScope) ? (
                    `All ${existing.target}s`
                  ) : (
                    <Button
                      type="button"
                      basic
                      size="tiny"
                      icon="eye"
                      content={`${Object.keys(existingScope).length} filter(s)`}
                      onClick={() => setShowScope(true)}
                    />
                  )}
                  <p>
                    <small>To change the scope, delete the rule and create a new one.</small>
                  </p>
                </Form.Field>
                {showScope && (
                  <AssignedFiltersForJobView deploymentFilters={existingScope} title={filtersTitle[existing.target]} onClose={() => setShowScope(false)} />
                )}
              </>
            )}
            {!existing && (
              <Form.Field>
                <label>Scope</label>
                <Checkbox
                  toggle
                  label={`Apply to all ${target}s (no filters)`}
                  checked={applyToAll}
                  onChange={(event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => setApplyToAll(!!data.checked)}
                />
              </Form.Field>
            )}
            {!existing && applyToAll && (
              <Message warning size="small">
                <Icon name="warning sign" />
                The notification will be shown on every {target} on the platform, including free tier. Double-check the text and expiration.
              </Message>
            )}
          </Form>
        </ContentSegment>
        {!existing && target === "organization" && !applyToAll && (
          <>
            <StyledFilterHeader>
              Organization filters <StyledIcon name="asterisk" color="orange" size="tiny" />
            </StyledFilterHeader>
            <OrganizationList
              {...props}
              filterView
              selectedFilters={organizationFilter}
              onFilterChange={setOrganizationFilter}
              onOrganizationSelected={(id: string): void => {}}
            />
          </>
        )}
        {!existing && target === "deployment" && !applyToAll && (
          <>
            <StyledFilterHeader>
              Deployment filters <StyledIcon name="asterisk" color="orange" size="tiny" />
            </StyledFilterHeader>
            <DeploymentList
              {...props}
              filterView
              onDeploymentSelected={(id: string): void => {}}
              onOrganizationSelected={(id: string): void => {}}
              onProjectSelected={(id: string): void => {}}
              onDataClusterSelected={(id: string): void => {}}
              selectedFilters={deploymentFilter}
              onFilterChange={(filterOptions: ListAllDeploymentsRequest) => setDeploymentFilter(omit(filterOptions, ["options"]))}
            />
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <FormActionButtonCancel icon="close" title="Cancel" onClick={props.onClose} />
        {existing ? (
          <FormActionButtonSave primary icon="check" title="Save Rule" disabled={saving} onClick={save} />
        ) : (
          <FormActionButtonCreate primary icon="plus" title="Create Rule" disabled={saving} onClick={save} />
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default CreateNotificationRuleModal;
